import {
  Badge,
  Button,
  Container,
  LoadingOverlay,
  ScrollArea,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { useFetch } from '@mantine/hooks';
import { useMemo } from 'react';
import PageHeader from '@components/shared/PageHeader';
import { Link } from 'react-router-dom';
import {
  MOCK_CERTIFICATE_TEMPLATES,
  type MockCertificateTemplate,
} from './mockCertificateTemplates';
import { config } from '../config/env';
import type { ICourse } from '../interfaces/ICourse';
import { getCertificateTemplateId } from '../certificateTemplates/courseCertificationDetails';

type CourseWithCertificate = ICourse & { certificate?: string | null };

function getTemplateStatus(
  associatedCoursesCount: number
): 'active' | 'non active' {
  return associatedCoursesCount > 0 ? 'active' : 'non active';
}

function statusColor(status: 'active' | 'non active'): string {
  return status === 'active' ? 'teal' : 'gray';
}

export default function AdminCertificateTemplates() {
  const { data: allCoursesData, loading } = useFetch<{
    data: CourseWithCertificate[];
  }>(`${config.API_URL}/services/userCourses`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  const associatedCountByTemplateId = useMemo(() => {
    const counts = new Map<MockCertificateTemplate['templateId'], number>();
    for (const template of MOCK_CERTIFICATE_TEMPLATES) {
      counts.set(template.templateId, 0);
    }
    for (const course of allCoursesData?.data ?? []) {
      const templateId = getCertificateTemplateId(course.certificate);
      counts.set(templateId, (counts.get(templateId) ?? 0) + 1);
    }
    return counts;
  }, [allCoursesData]);

  return (
    <Container size='2xl' py='lg' style={{ position: 'relative' }}>
      <LoadingOverlay visible={loading} zIndex={10} />

      <Stack gap='lg'>
        <PageHeader
          title='Certificate Templates'
          subtitle='Elenco template certificato (mock) per mappare course.id -> templateId.'
          breadcrumbs={[
            { label: 'Admin', href: '/admin' },
            { label: 'Courses', href: '/admin/courses' },
            { label: 'Certificate Templates' },
          ]}
          showBackButton={false}
        />

        <Text size='sm' c='dimmed'>
          I corsi associati sono calcolati dai corsi reali in base al valore
          certificato del corso.
        </Text>

        <ScrollArea type='scroll' scrollbars='xy' offsetScrollbars>
          <Table striped highlightOnHover withTableBorder miw={720}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Template ID</Table.Th>
                <Table.Th>Nome</Table.Th>
                <Table.Th>Categoria</Table.Th>
                <Table.Th>Stato</Table.Th>
                <Table.Th ta='right'>Corsi associati</Table.Th>
                <Table.Th>Ultimo aggiornamento</Table.Th>
                <Table.Th ta='right'>Azioni</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {MOCK_CERTIFICATE_TEMPLATES.map(template => {
                const associatedCoursesCount =
                  associatedCountByTemplateId.get(template.templateId) ?? 0;
                const status = getTemplateStatus(associatedCoursesCount);
                return (
                  <Table.Tr key={template.id}>
                    <Table.Td>
                      <Text ff='monospace' size='sm'>
                        {template.templateId}
                      </Text>
                    </Table.Td>
                    <Table.Td>{template.displayName}</Table.Td>
                    <Table.Td>{template.category}</Table.Td>
                    <Table.Td>
                      <Badge color={statusColor(status)} variant='light'>
                        {status}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta='right'>{associatedCoursesCount}</Table.Td>
                    <Table.Td>{template.lastUpdated}</Table.Td>
                    <Table.Td ta='right'>
                      <Button
                        size='xs'
                        variant='light'
                        component={Link}
                        to={`/admin/certificate-templates/${template.id}`}
                      >
                        Apri
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      </Stack>
    </Container>
  );
}
